import L from "leaflet";
import { OpenLocationCode } from "open-location-code";
import type { RouteContext } from "../routing";
import type { ParsedPoi } from "./types";
import { formatDuration, getPoiColorClass, KIND_FILL } from "./types";
import { makePoiIconWithColor } from "./icons";
import {
  getPoiListEl,
  getMap,
  notifyVisibilityChange,
  getVisiblePois,
  setListContext,
} from "./state";
import { isPoiSelected, togglePoiSelected } from "./selection";

type PoiClickFn = (poi: ParsedPoi) => void;
let _poiClickHandler: PoiClickFn | null = null;

export function setPoiClickHandler(fn: PoiClickFn): void {
  _poiClickHandler = fn;
}

function dotStyle(kind: import("../types").PoiKind): string {
  return `width:12px;height:12px;border-radius:50%;flex-shrink:0;margin-top:4px;background:${KIND_FILL[kind]};`;
}

// ─── Content builder ──────────────────────────────────────────────────────────

export function buildPoiContent(poi: ParsedPoi, timeStr: string): HTMLElement {
  const el = document.createElement("div");
  el.dataset.poiItem = poi.id;
  if (poi.liveMissed) el.classList.add("poi-missed");

  const kindLabel =
    poi.kind === "mcd"    ? "McDonald's"
    : poi.kind === "orlen"  ? "Orlen"
    : poi.kind === "toilet" ? "Toaleta"
    : "Fuel station";

  const tagsLines = Object.entries(poi.tags)
    .map(([k, v]) => `<span class="tag-k">${k}</span>: ${v}`)
    .join("<br>");

  const osmUrl   = `https://www.openstreetmap.org/${poi.osmType}/${poi.id}`;
  const gmapsUrl  = `https://www.google.com/maps?q=${poi.lat},${poi.lng}`;
  const orlenLink = poi.kind === "orlen"
    ? `<a class="poi-action-btn" href="https://www.orlen.pl/pl/dla-ciebie/stacje?kw=${encodeURIComponent(new OpenLocationCode().encode(poi.lat, poi.lng))}&from=&to=&s=&wp=&dst=0" target="_blank" rel="noopener">⛽ Orlen</a>`
    : "";

  let detourHtml: string;
  if (!poi.detour) {
    detourHtml = `<span class="detour-loading">obliczanie…</span>`;
  } else if (!poi.detour.reachable) {
    detourHtml = `<span class="detour-unreachable">nie znaleziono trasy</span>`;
  } else {
    // Use live ETA when set; fall back to static arrival time from start
    const etaS = poi.liveEtaS ?? poi.detour.arrivalS;
    const isLive = poi.liveEtaS !== undefined;
    const prefix = isLive ? "przyjazd w ok. " : "przyjazd w ";
    // In live mode, append the absolute clock time of expected arrival
    const liveTimeStr = isLive
      ? ` (<span class="detour-clock">${new Date(Date.now() + etaS * 1000).toLocaleTimeString("pl-PL", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Warsaw" })}</span>)`
      : "";
    detourHtml = `
      <span class="detour-arrive">${prefix}${formatDuration(etaS)}${liveTimeStr}</span>
      <span class="detour-sep"> · </span>
      <span class="detour-cost" title="Dodatkowy czas w porównaniu z przejazdem bez postoju">
        dokłada +${formatDuration(poi.detour.detourCostS)}
      </span>`;
  }

  const isSelected = isPoiSelected(poi.id);

  el.innerHTML = `
    <div class="poi-content-header">
      <input type="checkbox" class="poi-select-cb" data-poi-id="${poi.id}"${isSelected ? " checked" : ""}
             title="Zaznacz, aby pokazać na mapie">
      <div class="poi-name">${poi.name}${poi.liveMissed ? " (minięto)" : ""}</div>
      <div class="poi-meta">${poi.amenity || "—"} · ${kindLabel}</div>
    </div>
    <div class="poi-detour-row">${detourHtml}</div>
    <div class="poi-actions">
      <a class="poi-action-btn" href="${gmapsUrl}" target="_blank" rel="noopener">🗺️ Maps</a>
      ${orlenLink}
      ${poi.tags.website ? `<a class="poi-action-btn" href="${poi.tags.website}" target="_blank" rel="noopener">🌐 Strona</a>` : ""}
      <button class="poi-action-btn poi-details-btn">Metadane</button>
    </div>
    <div class="poi-tags" style="display:none">
      <a class="poi-action-btn" href="${osmUrl}" target="_blank" rel="noopener">📍 OSM</a>
      <pre>${tagsLines}</pre>
    </div>`;

  const cb = el.querySelector(`.poi-select-cb[data-poi-id="${poi.id}"]`) as HTMLInputElement;
  cb.addEventListener("change", () => { togglePoiSelected(poi.id); notifyVisibilityChange(); });

  const detailsBtn = el.querySelector(".poi-details-btn") as HTMLButtonElement;
  const tagsDiv    = el.querySelector(".poi-tags") as HTMLDivElement;
  detailsBtn.addEventListener("click", (e) => {
    e.stopPropagation();
    const hidden = tagsDiv.style.display === "none";
    tagsDiv.style.display = hidden ? "block" : "none";
    detailsBtn.textContent = hidden ? "Ukryj" : "Metadane";
  });

  el.style.cssText = "padding:10px 16px;border-bottom:1px solid #0f172a;cursor:pointer;";
  el.addEventListener("click", (e) => {
    const target = e.target as HTMLElement;
    if (target.classList.contains("poi-select-cb")) return;
    const map = getMap();
    if (map) map.setView([poi.lat, poi.lng], 15, { animate: true });
    _poiClickHandler?.(poi);
  });

  return el;
}

// ─── List rendering ───────────────────────────────────────────────────────────

export function rerenderPoiList(): void {
  renderList(getVisiblePois());
}

function renderList(pois: ParsedPoi[]): void {
  const listEl = getPoiListEl();
  const map    = getMap();
  if (!listEl || !map) return;

  listEl.innerHTML = "";
  if (pois.length === 0) {
    listEl.innerHTML = `<div style="padding:16px;font-size:13px;color:#64748b;text-align:center">Brak miejsc spełniających kryteria</div>`;
    return;
  }

  for (const poi of pois) {
    const timeStr = poi.detour?.reachable
      ? formatDuration(poi.detour.arrivalS)
      : (poi.detour ? "nie znaleziono trasy" : "obliczanie…");

    const el = buildPoiContent(poi, timeStr);

    const header = el.querySelector(".poi-content-header") as HTMLElement;
    header.style.cssText = "display:flex;align-items:flex-start;gap:10px;";
    const dot = document.createElement("div");
    dot.style.cssText = dotStyle(poi.kind);
    header.prepend(dot);

    el.classList.add(getPoiColorClass(poi));
    listEl.appendChild(el);
  }
}

export function rerenderMarkers(poiLayer: L.LayerGroup, routeCtx: RouteContext | null): void {
  if (!routeCtx) return;
  renderPois(getVisiblePois(), routeCtx, poiLayer);
}

// ─── Map markers ──────────────────────────────────────────────────────────────

export function renderPois(
  pois:     ParsedPoi[],
  routeCtx: RouteContext,
  poiLayer: L.LayerGroup,
): void {
  for (const poi of pois) {
    const timeStr = poi.detour?.reachable
      ? formatDuration(poi.detour.arrivalS)
      : "—";
    const content = buildPoiContent(poi, timeStr);
    const icon    = makePoiIconWithColor(poi);
    const marker  = L.marker([poi.lat, poi.lng], { icon, poiId: poi.id } as L.MarkerOptions & { poiId: string })
      .bindPopup(content, { maxWidth: 320 });
    marker.on("click", () => _poiClickHandler?.(poi));
    poiLayer.addLayer(marker);
  }
}

// ─── POI list initialisation ─────────────────────────────────────────────────

export function setPoiList(
  pois:      ParsedPoi[],
  _routeCtx: RouteContext | null,
  listEl:    HTMLDivElement,
  countEl:   HTMLSpanElement,
  map:       L.Map,
): void {
  setListContext(pois, listEl, map);
  countEl.textContent = String(pois.length);
  renderList(pois);
}

// ─── POI sorting ─────────────────────────────────────────────────────────────

export function sortPoisByArrival(pois: ParsedPoi[]): void {
  pois.sort((a, b) => {
    const aR = a.detour?.reachable ?? false;
    const bR = b.detour?.reachable ?? false;
    if (aR !== bR) return aR ? -1 : 1;
    if (aR) {
      const aA = a.detour!.arrivalS;
      const bA = b.detour!.arrivalS;
      if (aA !== bA) return aA - bA;
    }
    return a.routeDistM - b.routeDistM;
  });
}
